<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import InputOtp from 'primevue/inputotp';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { computed, toRef } from 'vue';

import { UiButton, UiDialogShell, UiResendButton } from '@peanuds/ui';

import { useTotalBalanceEmailVerificationDialog } from './useTotalBalanceEmailVerificationDialog';

interface Props {
  visible: boolean;
}

defineOptions({ name: 'TotalBalanceEmailVerificationDialog' });

const { t } = useTranslation();
const props = defineProps<Props>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
}>();

const {
  step,
  otpValue,
  emailDraft,
  displayedEmail,
  otpVerificationStatus,
  isOtpInteractionDisabled,
  isOtpVerificationFinalizing,
  isSubmitting,
  errorMessage,
  otpResetKey,
  resendResetKey,
  isOtpContinueDisabled,
  isEmailContinueDisabled,
  openChat,
  openUpdateEmailStep,
  returnToVerificationStep,
  handleOtpContinue,
  handleEmailContinue,
  handleResend,
} = useTotalBalanceEmailVerificationDialog(toRef(props, 'visible'));

const dialogTitle = computed(() =>
  step.value === 'updateEmail' ? 'Update Email Address' : 'Verify Email',
);

const otpContinueLabel = computed(() => {
  if (otpVerificationStatus.value === 'verified') {
    return 'Verified';
  }

  if (otpVerificationStatus.value === 'verifying') {
    return 'Verifying';
  }

  return 'Continue';
});

const otpContinueIcon = computed(() => {
  if (otpVerificationStatus.value === 'verified') {
    return 'pi pi-check email-verification__verified-icon';
  }

  if (otpVerificationStatus.value === 'verifying') {
    return 'pi pi-spin pi-spinner email-verification__verifying-icon';
  }

  return undefined;
});

const isOtpVerified = computed(() => otpVerificationStatus.value === 'verified');

const otpContinueSeverity = computed(() => (isOtpVerified.value ? 'success' : undefined));
</script>

<template>
  <UiDialogShell
    :visible="visible"
    :title="dialogTitle"
    close-label="Close dialog"
    :close-disabled="isOtpVerificationFinalizing"
    :dismissable-mask="false"
    test-id="email-verification-dialog"
    @update:visible="emit('update:visible', $event)"
  >
    <template #body>
      <div class="email-verification">
        <template v-if="step === 'currentEmail'">
          <div class="email-verification__header">
            <p class="email-verification__subtitle">
              We sent a verification email to
              <strong class="email-verification__email" data-testid="email-verification-recipient">
                {{ displayedEmail }}
              </strong>
            </p>
            <button
              type="button"
              class="email-verification__update-email-button"
              data-testid="email-verification-update-email"
              :disabled="isOtpInteractionDisabled"
              @click="openUpdateEmailStep"
            >
              Update email
            </button>
          </div>

          <small v-if="errorMessage" class="email-verification__error" role="alert">
            {{ errorMessage }}
          </small>

          <div class="email-verification__resend-section">
            <p class="email-verification__resend-label">Didn’t receive the email?</p>
            <div class="email-verification__resend-actions">
              <UiResendButton
                outlined
                severity="secondary"
                class="email-verification__resend-button"
                data-testid="email-verification-resend"
                :active="visible"
                :disabled="isSubmitting || isOtpInteractionDisabled"
                :reset-key="resendResetKey"
                restart-on-click
                @resend="handleResend"
              />

              <UiButton
                label="Contact our support team"
                variant="ghost"
                class="email-verification__support-button"
                :disabled="isOtpInteractionDisabled"
                @click="openChat"
              />
            </div>
          </div>
        </template>

        <template v-else-if="step === 'otp'">
          <div class="email-verification__header">
            <p class="email-verification__subtitle">
              We sent a 6-digit code to
              <strong class="email-verification__email" data-testid="email-verification-recipient">
                {{ displayedEmail }}
              </strong>
            </p>
            <button
              type="button"
              class="email-verification__update-email-button"
              data-testid="email-verification-update-email"
              :disabled="isOtpInteractionDisabled"
              @click="openUpdateEmailStep"
            >
              Update email
            </button>
          </div>

          <div class="email-verification__otp-section">
            <Message
              severity="info"
              size="small"
              class="email-verification__account-refresh-message"
            >
              {{ t('totalBalance.email_verification.account_refresh_notice') }}
            </Message>

            <InputOtp
              v-model="otpValue"
              :key="otpResetKey"
              :length="6"
              class="email-verification__otp-input"
              data-testid="email-verification-otp"
              :disabled="isOtpInteractionDisabled"
              integerOnly
            />

            <UiButton
              :label="otpContinueLabel"
              :icon="otpContinueIcon"
              :severity="otpContinueSeverity"
              :class="[
                'email-verification__continue',
                { 'email-verification__continue--verified': isOtpVerified },
              ]"
              data-testid="email-verification-continue"
              :disabled="isOtpContinueDisabled"
              @click="handleOtpContinue"
            />

            <small v-if="errorMessage" class="email-verification__error" role="alert">
              {{ errorMessage }}
            </small>
          </div>

          <div class="email-verification__resend-section">
            <p class="email-verification__resend-label">Didn’t receive a code?</p>
            <div class="email-verification__resend-actions">
              <UiResendButton
                outlined
                severity="secondary"
                class="email-verification__resend-button"
                data-testid="email-verification-resend"
                :active="visible"
                :disabled="isSubmitting || isOtpInteractionDisabled"
                :reset-key="resendResetKey"
                restart-on-click
                @resend="handleResend"
              />

              <UiButton
                label="Contact our support team"
                variant="ghost"
                class="email-verification__support-button"
                :disabled="isOtpInteractionDisabled"
                @click="openChat"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <div class="email-verification__header">
            <p class="email-verification__subtitle">
              Update the email address associated with your account. A verification email will be
              sent to the new address.
            </p>
          </div>

          <div class="email-verification__email-field">
            <label class="email-verification__email-label" for="total-balance-email-update">
              New Email
            </label>
            <InputText
              id="total-balance-email-update"
              v-model="emailDraft"
              type="email"
              class="email-verification__email-input"
              data-testid="email-verification-email-input"
              :disabled="isSubmitting"
            />

            <small v-if="errorMessage" class="email-verification__error" role="alert">
              {{ errorMessage }}
            </small>
          </div>

          <div class="email-verification__update-actions">
            <UiButton
              label="Return Back"
              severity="secondary"
              variant="outlined"
              class="email-verification__return-button"
              data-testid="email-verification-return"
              :disabled="isSubmitting"
              @click="returnToVerificationStep"
            />
            <UiButton
              label="Continue"
              class="email-verification__update-submit"
              data-testid="email-verification-update-submit"
              :loading="isSubmitting"
              :disabled="isEmailContinueDisabled"
              @click="handleEmailContinue"
            />
          </div>
        </template>
      </div>
    </template>
  </UiDialogShell>
</template>

<style scoped lang="scss">
.email-verification {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $static-space-8;
  width: 100%;

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-4;
    width: 100%;
  }

  &__subtitle {
    margin: 0;
    color: var(--p-surface-600);
    font-size: $static-text-sm;
    line-height: $leading-normal;
    letter-spacing: $tracking-tight;
    text-align: center;
  }

  &__email {
    color: var(--p-surface-900);
    font-weight: $font-semibold;
  }

  &__update-email-button {
    border: 0;
    background: transparent;
    color: var(--p-primary-color);
    cursor: pointer;
    font: inherit;
    font-size: $static-text-sm;
    font-weight: $font-medium;
    line-height: $leading-none;
    padding: 0;
    text-decoration: underline;

    &:disabled {
      cursor: default;
      opacity: 0.7;
    }
  }

  &__otp-section,
  &__email-field {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-4;
    width: 100%;
  }

  &__account-refresh-message {
    width: 100%;
  }

  &__otp-input {
    display: flex;
    gap: 0.4375rem;

    :deep(.p-inputtext) {
      width: 2.625rem;
      height: $static-space-12;
      border-color: var(--p-surface-300);
      border-radius: $radius-xl;
      background: var(--p-surface-0);
      box-shadow: 0 1px 2px 0 rgba(18, 18, 23, 0.05);
      color: var(--p-surface-900);
      font-size: $static-text-lg;
      font-weight: $font-medium;
    }
  }

  &__continue {
    width: 100%;
    height: 2.375rem;
    border-radius: $radius-full;
    font-size: $static-text-sm;
    font-weight: $font-medium;
    gap: 0.4375rem;
    letter-spacing: $tracking-tight;
  }

  :deep(.p-component.p-button.email-verification__continue--verified) {
    border-color: var(--p-green-500, #22c55e);
    background: var(--p-green-500, #22c55e);
    color: var(--p-surface-0);
  }

  :deep(.email-verification__verifying-icon),
  :deep(.email-verification__verified-icon) {
    width: 0.875rem;
    height: 0.875rem;
    color: var(--p-surface-0);
    font-size: 0.875rem;
  }

  &__continue:disabled,
  &__update-submit:disabled {
    opacity: 0.7;
  }

  &__resend-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $static-space-3;
  }

  &__resend-label {
    width: 21.1875rem;
    margin: 0;
    color: var(--p-surface-900);
    font-size: $static-text-sm;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
    text-align: center;
  }

  &__resend-actions,
  &__update-actions {
    display: flex;
    justify-content: center;
    gap: $static-space-3;
    width: 100%;
  }

  &__resend-button,
  &__support-button,
  &__return-button,
  &__update-submit {
    height: $static-space-8;
    font-size: $static-text-sm;
    font-weight: $font-medium;
    letter-spacing: $tracking-tight;
  }

  &__support-button {
    color: var(--p-purple-500);
  }

  &__email-field {
    align-items: stretch;
  }

  &__email-label {
    padding-left: $static-space-3;
    color: var(--p-surface-900);
    font-size: $static-text-sm;
    font-weight: $font-medium;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
  }

  &__email-input {
    width: 100%;
    height: $static-space-10;
    border-color: var(--p-surface-300);
    border-radius: $radius-xl;
    background: var(--p-surface-0);
    box-shadow: 0 1px 2px 0 rgba(18, 18, 23, 0.05);
  }

  &__return-button,
  &__update-submit {
    min-width: 6.625rem;
  }

  &__error {
    display: block;
    color: var(--p-red-500);
    font-size: $text-sm;
    line-height: $leading-normal;
    text-align: center;
  }
}
</style>
